import moment from 'moment'
import { LinedSearch, SolidCalendar } from '@rushable/icons'
import DatePicker from 'antd/es/date-picker'
import TimePicker from 'antd/es/time-picker'
import Button from 'components/Button'
import SingleInput from 'components/SingleInput'
import { Select } from 'components/Select'
import { IOptions } from 'components/Select/help'
import Switch from 'components/Switch'
import {
  AI_CALL_STATUS_OPTIONS,
  TFilters,
  TViewMode,
} from 'pages/AiCalls/helpers/constant'

export type TAiCallsFilterProp = {
  filters: TFilters
  mode: TViewMode
  assigneeOptions: IOptions[]
  handleFormChange: (key: string, value: any) => void
  handleReset: () => void
}

export default function AiCallsFilter({
  filters,
  mode,
  assigneeOptions,
  handleFormChange,
  handleReset,
}: TAiCallsFilterProp): JSX.Element {
  const dateRangeValue =
    filters.from && filters.to ? [moment(filters.from), moment(filters.to)] : null
  const isDaily = mode === 'daily'
  const isDailyDrillDown = isDaily && !!filters.date
  const isDailySummary = isDaily && !filters.date
  const dailyDateValue =
    isFinite(moment(filters.from).valueOf()) &&
    filters.from &&
    moment(filters.from).isSame(filters.to, 'day')
      ? moment(filters.from)
      : null
  const timeRangeValue =
    filters.from && filters.to ? [moment(filters.from), moment(filters.to)] : null

  return (
    <div className='mb-8 mr-4 flex items-start justify-between gap-4'>
      <div className='flex flex-wrap items-start gap-4'>
        {!isDailySummary && (
          <>
            <SingleInput
              name='Keyword'
              className='w-216'
              placeholder='Brand, caller, callback'
              iconAfter={<LinedSearch className='text-light-300' size={16} />}
              value={filters.keyword}
              onChange={e => handleFormChange('keyword', e.target.value)}
            />
            <Select
              className='w-60'
              value={filters.status}
              options={AI_CALL_STATUS_OPTIONS}
              placeholder='Status'
              onChange={value => handleFormChange('status', value)}
            />
            <Select
              className='w-60'
              value={filters.assignee_crm_user_id}
              options={assigneeOptions}
              placeholder='Assignee'
              isPerson
              onChange={value => handleFormChange('assignee_crm_user_id', value)}
            />
          </>
        )}
        {isDailySummary ? (
          <DatePicker
            className='w-216 py-2 px-4 bg-dark-300 border-2 border-dark-300 text-light-100 rounded-lg hover:border-light-700'
            style={{ color: '#4C515C', borderColor: 'transparent' }}
            value={dailyDateValue as any}
            inputReadOnly
            suffixIcon={<SolidCalendar className='text-light-300' size={16} />}
            format='MMM DD, YYYY'
            placeholder='Date'
            onChange={(date: any) => {
              handleFormChange('date_range', {
                from: date ? `${moment(date).format('YYYY-MM-DD')} 00:00:00` : '',
                to: date ? `${moment(date).format('YYYY-MM-DD')} 23:59:59` : '',
              })
            }}
          />
        ) : isDailyDrillDown ? (
          <TimePicker.RangePicker
            className='w-256 py-2 px-4 bg-dark-300 border-2 border-dark-300 text-light-100 rounded-lg hover:border-light-700'
            style={{ color: '#4C515C', borderColor: 'transparent' }}
            value={timeRangeValue as any}
            inputReadOnly
            format='HH:mm'
            placeholder={['From', 'To']}
            onChange={(times: any) => {
              handleFormChange('date_range', {
                from: times?.[0]
                  ? `${filters.date} ${moment(times[0]).format('HH:mm')}:00`
                  : '',
                to: times?.[1]
                  ? `${filters.date} ${moment(times[1]).format('HH:mm')}:00`
                  : '',
              })
            }}
          />
        ) : (
          <DatePicker.RangePicker
            className='w-256 py-2 px-4 bg-dark-300 border-2 border-dark-300 text-light-100 rounded-lg hover:border-light-700'
            style={{ color: '#4C515C', borderColor: 'transparent' }}
            value={dateRangeValue as any}
            inputReadOnly
            suffixIcon={<SolidCalendar className='text-light-300' size={16} />}
            showTime={{ format: 'HH:mm' }}
            format='MMM DD, YYYY HH:mm'
            placeholder={['From', 'To']}
            onChange={(dates: any) => {
              handleFormChange('date_range', {
                from: dates?.[0]
                  ? moment(dates[0]).format('YYYY-MM-DD HH:mm:ss')
                  : '',
                to: dates?.[1]
                  ? moment(dates[1]).format('YYYY-MM-DD HH:mm:ss')
                  : '',
              })
            }}
          />
        )}
        <Button color='secondary' theme='outlined' onClick={handleReset}>
          CLEAR FILTERS
        </Button>
      </div>
      {!isDailyDrillDown && (
        <div className='flex h-10 flex-shrink-0 items-center'>
          <Switch
            checked={!isDaily}
            onChange={e =>
              handleFormChange('mode', e.target.checked ? 'list' : 'daily')
            }
            labelClass='pl-3 text-sm'
          >
            {isDaily ? 'Daily Summary' : 'Calls List'}
          </Switch>
        </div>
      )}
    </div>
  )
}
